
import React, { useState, useEffect } from 'react';
import {
  Box, Container, Grid, Card, CardContent, CardActions, Typography, Button, Chip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, CircularProgress,IconButton,Paper
} from '@mui/material';
import { CheckCircle, Shield } from 'lucide-react';
import { fetchUserFromRedis } from "../HomePage/AuthonticationScreens/Register";
import CustomDialog from "../../CustomDialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const Activesubscriptionsapi = process.env.REACT_APP_GET_ACTIVE_SUBSCRIPTIONS_API;

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = process.env.REACT_APP_RAZORPAY_SCRIPT;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const ResidentSubscriptionView = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState('');
  const [userRole, setUserRole] = useState('resident');
  
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [userActiveSubscription, setUserActiveSubscription] = useState(null);
  const [userBenefits, setUserBenefits] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // CustomDialog state
  const [dialogState, setDialogState] = useState({
    open: false,
    message: '',
    type: 'info',
    title: 'Notification'
  });

  
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const storedUser = sessionStorage.getItem("user");
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }
  
        const userId = sessionStorage.getItem("userId");
        const token = sessionStorage.getItem("token");
  
        if (userId && token) {
          const userData = await fetchUserFromRedis(userId, token);
          if (userData) {
            setUser(userData);
            sessionStorage.setItem("user", JSON.stringify(userData));
            setCurrentUserId(userData.user_id || userData.id);
            setUserRole(userData.role || "resident");
          }
        }
      } catch (err) {
        console.error("Error fetching user:", err);
        setUser(null);
      } finally {
        setLoading(false); 
      }
    };
  
    fetchUser();
  }, []);
  

  // Fetch data when user is loaded
  useEffect(() => {
    if (user?.user_id && userRole === 'resident') {
      fetchActiveSubscriptions();
      fetchUserActiveSubscription();
      fetchUserBenefits();
    }
  }, [user, userRole]);

  const showNotification = (message, type = 'success', title = 'Notification') => {
    setDialogState({
      open: true,
      message,
      type,
      title
    });
  };

  const closeDialog = () => {
    setDialogState({ ...dialogState, open: false });
  };

  const fetchActiveSubscriptions = async () => {
    try {
      const response = await fetch(`${Activesubscriptionsapi}`);
      const data = await response.json();
      if (data.success) {
        setActiveSubscriptions(data.subscriptions);
      }
    } catch (error) {
      showNotification('Failed to fetch plans', 'error', 'Error');
    }
  };

  const fetchUserActiveSubscription = async () => {
    if (!currentUserId) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}/subscriptions/user-active?user_id=${currentUserId}`
      );
      const data = await response.json();
      if (data.success && data.has_active_subscription) {
        setUserActiveSubscription(data.subscription);
      } else {
        setUserActiveSubscription(null);
      }
    } catch (error) {
      console.error('Failed to fetch user subscription', error);
    }
  };

  const fetchUserBenefits = async () => {
    if (!currentUserId) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PRICING_BENIFITS_API}/${currentUserId}`
      );
      const data = await response.json();
      if (data.success) {
        setUserBenefits(data);
      }
    } catch (error) {
      console.error('Failed to fetch benefits', error);
    }
  };

  const handlePurchaseClick = async () => {
    if (!selectedPlan) return;
    
    setProcessingPayment(true);
    
    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        showNotification('Failed to load payment gateway', 'error', 'Payment Error');
        setProcessingPayment(false);
        return;
      }

      // Create Razorpay order
      const orderResponse = await fetch(`${process.env.REACT_APP_CREATE_SUBSCRIPTIONS_ORDER_API}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': currentUserId
        },
        body: JSON.stringify({
          subscription_id: selectedPlan.subscription_id
        })
      });

      const orderData = await orderResponse.json();
      
      if (!orderData.success) {
        showNotification(orderData.error || 'Failed to create order', 'error', 'Order Failed');
        setProcessingPayment(false);
        return;
      }

      // Razorpay options
      const options = {
        key: orderData.razorpay_key,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Ayuja Life Care',
        description: `${selectedPlan.name} Subscription`,
        order_id: orderData.order_id,
        handler: async (response) => {
          // Verify payment and create subscription
          try {
            const verifyResponse = await fetch(
              `${process.env.REACT_APP_PAYMENT_VERIFY_SUBSCRIPTIONS_API}`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-User-Id': currentUserId
                },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  subscription_id: selectedPlan.subscription_id,
                  payment_method: paymentMethod
                })
              }
            );

            const verifyData = await verifyResponse.json();
            
            if (verifyData.success) {
              showNotification('Subscription purchased successfully!', 'success', 'Success');
              setShowPurchaseModal(false);
              setSelectedPlan(null);
              fetchUserActiveSubscription();
              fetchUserBenefits();
            } else {
              showNotification(verifyData.error || 'Payment verification failed', 'error', 'Verification Failed');
            }
          } catch (error) {
            showNotification('Error verifying payment', 'error', 'Error');
          } finally {
            setProcessingPayment(false);
          }
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: user?.phone || ''
        },
        theme: {
          color: '#3b82f6'
        },
        modal: {
          ondismiss: () => {
            setProcessingPayment(false);
            showNotification('Payment cancelled', 'warning', 'Payment Cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Payment error:', error);
      showNotification('Error processing payment', 'error', 'Payment Error');
      setProcessingPayment(false);
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_CANCEL_SUBSCRIPTIONS_API}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': currentUserId
        },
        body: JSON.stringify({
          reason: 'User requested cancellation'
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        showNotification('Subscription cancelled successfully', 'success', 'Cancelled');
        setUserActiveSubscription(null);
        fetchUserBenefits();
      } else {
        showNotification(data.error || 'Failed to cancel', 'error', 'Cancellation Failed');
      }
    } catch (error) {
      showNotification('Error cancelling subscription', 'error', 'Error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Please log in to view subscriptions</Alert>
      </Container>
    );
  }

  if (userRole !== 'resident') {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          Subscription purchase is only available for residents. 
          {userRole === 'admin' && ' Use the admin panel to manage subscription plans.'}
        </Alert>
      </Container>
    );
  }

  return (
    <>
    <Paper elevation={2} sx={{bgcolor: "#fff",mb: 3,}}>
    <Container maxWidth="lg">
      <Box sx={{py: 3,display: "flex",alignItems: "center",justifyContent: "center",position: "relative",}}>
        <IconButton
          onClick={() => window.history.back()}
          sx={{position: "absolute",left: 0,backgroundColor: "white",boxShadow: 1,
            "&:hover": { backgroundColor: "#f5f5f5" },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ color: "#006D77", fontWeight: 600 }} >
        Subscription Plans
        </Typography>
      </Box>
    </Container>
  </Paper>

    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Active Subscription Banner */}
      {userActiveSubscription && (
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg , #006D77 0%,#66A5AD 100%, #99C0C8 100%)', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {userActiveSubscription.subscription_name}
                </Typography>
                <Typography sx={{ opacity: 0.9 }}>Active Subscription</Typography>
              </Box>
              <Shield size={64} style={{ opacity: 0.8 }} />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Days Remaining</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {userActiveSubscription.days_remaining}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>End Date</Typography>
                <Typography variant="h6" fontWeight="600">
                  {new Date(userActiveSubscription.end_date).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Savings</Typography>
                <Typography variant="h5" fontWeight="bold">
                  ₹{userActiveSubscription.total_savings.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={cancelSubscription}
                  sx={{ bgcolor: 'white', color: 'error.main', '&:hover': { bgcolor: 'grey.100' } }}
                >
                  Cancel Plan
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Benefits Display */}
      {userBenefits?.has_subscription && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Your Benefits
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={4}>
                <Box sx={{ p: 2, bgcolor: '#dbeafe', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Diagnostic Discount
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {userBenefits.benefits.diagnostic_discount}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={4}>
                <Box sx={{ p: 2, bgcolor: '#d1fae5', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Medicine Discount
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#10b981' }}>
                    {userBenefits.benefits.medicine_discount}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={4}>
                <Box sx={{ p: 2, bgcolor: '#f3e8ff', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Health Checkup
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#a855f7' }}>
                    {userBenefits.benefits.health_checkup_discount}%
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Available Plans */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4"sx={{ fontWeight: "900", color: "#22577A", ml: 2 }}>
          {userActiveSubscription ? 'Other Available Plans' : 'Choose Your Plan'}
        </Typography>
        <Typography color="text.secondary">
          Select the perfect plan for your healthcare needs
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {activeSubscriptions.map((plan) => (
          <Grid item xs={12} md={4} key={plan.subscription_id}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              border: '2px solid',
              borderColor: userActiveSubscription?.subscription_id === plan.subscription_id 
                ? 'success.main' 
                : 'transparent',
              bgcolor: userActiveSubscription?.subscription_id === plan.subscription_id 
                ? 'success.50' 
                : 'background.paper',
              '&:hover': { 
                borderColor: userActiveSubscription?.subscription_id === plan.subscription_id 
                  ? 'success.main' 
                  : 'primary.main',
                transform: 'translateY(-4px)',
                transition: 'all 0.3s'
              }
            }}>
              <CardContent sx={{ flexGrow: 1 }}>
                {userActiveSubscription?.subscription_id === plan.subscription_id && (
                  <Chip 
                    label="Active Plan" 
                    color="success" 
                    size="small" 
                    sx={{ mb: 1 }}
                    icon={<CheckCircle size={16} />}
                  />
                )}
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {plan.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                  {plan.description}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="#006D77">
                    ₹{plan.price_per_month}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Valid for {plan.validity_days} days
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                  Benefits:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {plan.features.diagnostic_discount_percent > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle size={18} color="#10b981" />
                      <Typography variant="body2">
                        {plan.features.diagnostic_discount_percent}% off Diagnostics
                      </Typography>
                    </Box>
                  )}
                  {plan.features.medicine_discount_percent > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle size={18} color="#10b981" />
                      <Typography variant="body2">
                        {plan.features.medicine_discount_percent}% off Medicines
                      </Typography>
                    </Box>
                  )}
                  {plan.features.health_checkup_discount > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle size={18} color="#10b981" />
                      <Typography variant="body2">
                        {plan.features.health_checkup_discount}% off Health Checkups
                      </Typography>
                    </Box>
                  )}
                  {plan.features.free_pickup && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle size={18} color="#10b981" />
                      <Typography variant="body2">Free Pickup</Typography>
                    </Box>
                  )}
                  {plan.features.priority_support && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle size={18} color="#10b981" />
                      <Typography variant="body2">Priority Support</Typography>
                    </Box>
                  )}
                  {plan.features.home_sample_collection && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle size={18} color="#10b981" />
                      <Typography variant="body2">Home Sample Collection</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ backgroundColor: "#006D77", textTransform: "none" }}
                  onClick={() => {
                    setSelectedPlan(plan);
                    setShowPurchaseModal(true);
                  }}
                  disabled={userActiveSubscription?.subscription_id === plan.subscription_id}
                >
                  {userActiveSubscription?.subscription_id === plan.subscription_id 
                    ? 'Current Plan' 
                    : userActiveSubscription 
                      ? 'Switch to This Plan' 
                      : 'Subscribe Now'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Payment Method Modal */}
      <Dialog 
        open={showPurchaseModal} 
        onClose={() => !processingPayment && setShowPurchaseModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight="bold">
            Purchase Subscription
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          {selectedPlan && (
            <Box sx={{ mt: 2 }}>
              {userActiveSubscription && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight="600" gutterBottom>
                    You are switching from {userActiveSubscription.subscription_name}
                  </Typography>
                  <Typography variant="body2">
                    Your current plan will be cancelled and you'll be charged for the new plan immediately.
                    Remaining days: {userActiveSubscription.days_remaining}
                  </Typography>
                </Alert>
              )}
              
              <Box sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText', mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  {selectedPlan.name}
                </Typography>
                <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                  ₹{selectedPlan.price_per_month}
                </Typography>
                <Typography variant="body2">
                  Valid for {selectedPlan.validity_days} days
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setShowPurchaseModal(false)} 
            variant="outlined"
            disabled={processingPayment}
            sx={{ color: "#006D77", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePurchaseClick} 
            sx={{ backgroundColor: "#006D77", textTransform: "none" }}
            variant="contained" 
            color="primary"
            disabled={processingPayment}
            startIcon={processingPayment && <CircularProgress size={20} />}
          >
            {processingPayment ? 'Processing...' : 'Proceed to Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CustomDialog for Notifications */}
      <CustomDialog
        open={dialogState.open}
        message={dialogState.message}
        type={dialogState.type}
        title={dialogState.title}
        onClose={closeDialog}
        buttonText="OK"
      />
    </Container>
    </>
  );
};

export default ResidentSubscriptionView;